import { STUDIO_V2_FORM_FIELD_TYPES } from '../components/studio/studioV2FormFieldTypes';

/** Flow `form` node historically supports a small type set in the inspector UI. */
export const STUDIO_V2_FLOW_FORM_FIELD_TYPES = [
  'text', 'textarea', 'number', 'email', 'select', 'combobox', 'checkbox', 'switch', 'date', 'slider', 'rating',
];

const BUILDER_TO_STUDIO = {
  text: 'text',
  email: 'email',
  password: 'password',
  url: 'url',
  textarea: 'textarea',
  number: 'number',
  date: 'date',
  time: 'time',
  datetime: 'datetime',
  checkbox: 'checkbox',
  switch: 'switch',
  slider: 'slider',
  rating: 'rating',
  listbox: 'select',
  combobox: 'combobox',
  radiobuttons: 'select',
  phone: 'phone',
  color: 'color',
  file: 'text',
  image: 'text',
  signature: 'text',
  hidden: 'text',
  custom: 'text',
  currency: 'currency',
};

function normalizeStudioType(raw) {
  const t = String(raw || 'text').toLowerCase();
  const mapped = BUILDER_TO_STUDIO[t] || t;
  if (STUDIO_V2_FORM_FIELD_TYPES.includes(mapped)) return mapped;
  return 'text';
}

function normalizeFlowType(studioType) {
  const t = String(studioType || 'text').toLowerCase();
  if (t === 'textarea') return 'textarea';
  if (t === 'number' || t === 'slider' || t === 'rating') return 'number';
  if (t === 'email') return 'email';
  if (t === 'select' || t === 'combobox' || t === 'checkboxgroup' || t === 'multiselect') return 'select';
  if (t === 'checkbox' || t === 'switch') return 'checkbox';
  if (t === 'date' || t === 'datetime' || t === 'time') return 'date';
  return 'text';
}

function collectGroupboxChildIds(formElements) {
  const ids = new Set();
  if (!Array.isArray(formElements)) return ids;
  for (const el of formElements) {
    if (el && el.type === 'groupbox' && Array.isArray(el.fields)) {
      for (const fid of el.fields) {
        if (fid) ids.add(String(fid));
      }
    }
  }
  return ids;
}

function optionsFromElement(el) {
  if (Array.isArray(el.options) && el.options.length) return el.options.map(String).filter(Boolean);
  if (Array.isArray(el.enum) && el.enum.length) return el.enum.map(String).filter(Boolean);
  return [];
}

function defaultFromElement(el) {
  if (!el || typeof el !== 'object') return undefined;
  if (el.defaultValue !== undefined) return el.defaultValue;
  if (el.default !== undefined) return el.default;
  if (el.value !== undefined && el.type === 'hidden') return el.value;
  return undefined;
}

function conditionFromElement(el) {
  return el?.showWhen || el?.showIf || el?.visibleWhen || undefined;
}

/**
 * Map one FormBuilder element to a Studio V2 app `formFields` item.
 * @param {object} el
 * @param {{ idSuffix?: string }} [opts]
 */
export function formElementToStudioV2AppField(el, opts = {}) {
  if (!el || typeof el !== 'object') return null;
  const suffix = opts.idSuffix || '';
  const baseId = String(el.id || el.key || 'field').replace(/\s+/g, '_');
  const id = `${baseId}${suffix}`;
  const type = normalizeStudioType(el.type);
  const optsList = ['select', 'combobox', 'checkboxgroup', 'multiselect'].includes(type)
    ? optionsFromElement(el)
    : [];
  return {
    id,
    name: id,
    type,
    label: String(el.label != null ? el.label : id),
    required: Boolean(el.required),
    placeholder: el.placeholder != null ? String(el.placeholder) : '',
    description: el.description != null ? String(el.description) : '',
    default: defaultFromElement(el),
    options: optsList,
    validation: el.validation && typeof el.validation === 'object' ? el.validation : undefined,
    showWhen: conditionFromElement(el),
    inputPath: `input.${id}`,
  };
}

/**
 * Flatten FormBuilder `formElements` into ordered Studio V2 app fields.
 * Skips layout-only entries where appropriate; expands `grid` columns and `groupbox` children.
 * @returns {{ fields: object[], warnings: string[] }}
 */
export function formElementsToStudioV2AppFields(formElements) {
  const warnings = [];
  if (!Array.isArray(formElements)) {
    return { fields: [], warnings: ['formElements was not an array'] };
  }
  const byId = {};
  for (const el of formElements) {
    if (el && el.id) byId[String(el.id)] = el;
  }
  const inGroupbox = collectGroupboxChildIds(formElements);
  const out = [];

  const pushField = (field) => {
    if (field) out.push(field);
  };

  for (const el of formElements) {
    if (!el || !el.type) continue;
    const id = String(el.id || '');

    if (el.type === 'grid') {
      const cols = Array.isArray(el.columns) ? el.columns : [];
      if (!cols.length) {
        warnings.push(`Grid "${id}" has no columns — skipped.`);
        continue;
      }
      for (const col of cols) {
        const key = String(col.key || col.id || '').trim();
        if (!key) continue;
        const colTypeRaw = String(col.type || 'string').toLowerCase();
        const colStudioType = colTypeRaw === 'number' || colTypeRaw === 'integer' ? 'number'
          : colTypeRaw === 'boolean' ? 'checkbox'
          : 'text';
        const synthetic = {
          id: `${id}_${key}`,
          type: colStudioType,
          label: col.label || key,
          required: Boolean(col.required),
          placeholder: col.placeholder || '',
          options: optionsFromElement(col),
        };
        pushField(formElementToStudioV2AppField(synthetic));
      }
      continue;
    }

    if (el.type === 'groupbox') {
      const fieldIds = Array.isArray(el.fields) ? el.fields : [];
      for (const fid of fieldIds) {
        const child = byId[String(fid)];
        if (!child) {
          warnings.push(`Groupbox "${id}" references missing field "${fid}".`);
          continue;
        }
        if (child.type === 'grid') {
          warnings.push(`Groupbox "${id}" contains grid "${fid}" — nested grids are skipped.`);
          continue;
        }
        if (child.type === 'groupbox') {
          warnings.push(`Nested groupbox "${fid}" under "${id}" — skipped.`);
          continue;
        }
        pushField(formElementToStudioV2AppField(child));
      }
      continue;
    }

    if (inGroupbox.has(id)) continue;
    if (el.type === 'hidden') {
      warnings.push(`Hidden field "${id}" omitted from Studio static form.`);
      continue;
    }

    pushField(formElementToStudioV2AppField(el));
  }

  return { fields: out, warnings };
}

/**
 * Convert app-style fields to flow `form` node `config.fields` shape.
 * @param {object[]} appFields from {@link formElementsToStudioV2AppFields}
 */
export function studioV2AppFieldsToFlowFields(appFields) {
  const list = Array.isArray(appFields) ? appFields : [];
  return list.map((f, i) => {
    const nameRaw = String(f.name || f.id || `field_${i + 1}`).replace(/[^a-zA-Z0-9_]/g, '_') || `field_${i + 1}`;
    return {
      name: nameRaw,
      label: String(f.label != null ? f.label : nameRaw),
      type: normalizeFlowType(f.type),
      required: Boolean(f.required),
      default: f.default != null ? String(f.default) : '',
      options: Array.isArray(f.options) ? f.options : [],
      description: f.description || '',
      placeholder: f.placeholder || '',
      showWhen: f.showWhen,
      inputPath: f.inputPath || `input.${nameRaw}`,
    };
  });
}

export function buildFormPayloadContract(formElements, opts = {}) {
  const { fields, warnings } = formElementsToStudioV2AppFields(formElements);
  const properties = {};
  const required = [];
  fields.forEach((field) => {
    const schema = {
      title: field.label,
      description: field.description || undefined,
    };
    if (field.type === 'number' || field.type === 'slider' || field.type === 'currency' || field.type === 'rating') schema.type = 'number';
    else if (field.type === 'checkbox' || field.type === 'switch') schema.type = 'boolean';
    else if (field.type === 'checkboxgroup' || field.type === 'multiselect') {
      schema.type = 'array';
      schema.items = { type: 'string' };
    } else schema.type = 'string';
    if (Array.isArray(field.options) && field.options.length && schema.type === 'string') schema.enum = field.options;
    if (field.default !== undefined) schema.default = field.default;
    if (field.showWhen) schema.showWhen = field.showWhen;
    properties[field.id] = schema;
    if (field.required) required.push(field.id);
  });
  return {
    schemaVersion: 'form-payload/v1',
    id: opts.id || 'form_payload',
    name: opts.name || 'Form payload',
    fields,
    inputMapping: fields.map((field) => ({
      fieldId: field.id,
      targetPath: field.inputPath || `input.${field.id}`,
      required: Boolean(field.required),
      type: field.type,
    })),
    jsonSchema: {
      type: 'object',
      additionalProperties: true,
      properties,
      required,
    },
    warnings,
  };
}

export function validateFormDesignContract(formElements) {
  const errors = [];
  const warnings = [];
  const seen = new Set();
  const ids = new Set();
  (Array.isArray(formElements) ? formElements : []).forEach((el, index) => {
    if (!el || typeof el !== 'object') return;
    const id = String(el.id || el.key || '').trim();
    if (!id) {
      errors.push(`Element #${index + 1} is missing id/key.`);
      return;
    }
    if (seen.has(id)) errors.push(`Duplicate field id "${id}".`);
    seen.add(id);
    ids.add(id);
    if (!String(el.label || '').trim() && !['hidden', 'divider', 'groupbox'].includes(el.type)) {
      warnings.push(`Field "${id}" is missing a label.`);
    }
    const opts = optionsFromElement(el);
    if (['listbox', 'combobox', 'radiobuttons', 'select'].includes(String(el.type || '').toLowerCase()) && opts.length === 0) {
      warnings.push(`Choice field "${id}" has no options.`);
    }
    const def = defaultFromElement(el);
    if (def !== undefined && opts.length && !opts.includes(String(def))) {
      warnings.push(`Default for "${id}" is not in its options.`);
    }
  });
  (Array.isArray(formElements) ? formElements : []).forEach((el) => {
    const condition = conditionFromElement(el);
    if (condition && typeof condition === 'object') {
      const fieldRef = String(condition.field || condition.fieldId || condition.source || '').trim();
      if (fieldRef && !ids.has(fieldRef)) warnings.push(`Condition on "${el.id}" references missing field "${fieldRef}".`);
    }
  });
  return { valid: errors.length === 0, errors, warnings };
}

export function generateDebugPayloadFromFormFields(fields) {
  const payload = {};
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    const key = String(field.id || field.name || '').trim();
    if (!key) return;
    if (field.default !== undefined && field.default !== '') payload[key] = field.default;
    else if (field.type === 'number' || field.type === 'slider' || field.type === 'currency' || field.type === 'rating') payload[key] = 0;
    else if (field.type === 'checkbox' || field.type === 'switch') payload[key] = false;
    else if (Array.isArray(field.options) && field.options.length) payload[key] = field.options[0];
    else payload[key] = '';
  });
  return { input: payload };
}

/**
 * @param {object[]} formElements
 * @param {'app'|'flow'} mode
 */
export function formElementsToStudioV2Fields(formElements, mode = 'app') {
  const { fields: appFields, warnings } = formElementsToStudioV2AppFields(formElements);
  if (mode === 'flow') {
    return { fields: studioV2AppFieldsToFlowFields(appFields), warnings };
  }
  return { fields: appFields, warnings };
}
